import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus, Trash2, Copy, Mail, Clock, ShieldCheck, User as UserIcon,
} from "lucide-react";
import { T } from "@/lib/testIds";

const roleTone = {
  owner: "bg-[#ECECFE] text-[#4338CA] border-[#C7D2FE]",
  admin: "bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]",
  staff: "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]",
};

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "staff" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/team/members")];
      if (isAdmin) reqs.push(api.get("/team/invites"));
      const results = await Promise.all(reqs);
      setMembers(results[0].data);
      setInvites(isAdmin ? results[1].data : []);
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/team/invites", form);
      const link = `${window.location.origin}/accept-invite?token=${r.data.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success("Invite created — link copied to clipboard");
      setForm({ email: "", role: "staff" });
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this pending invite?")) return;
    try {
      await api.delete(`/team/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this member from the workspace?")) return;
    try {
      await api.delete(`/team/members/${id}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/team/members/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const copyLink = async (token) => {
    const link = `${window.location.origin}/accept-invite?token=${token}`;
    await navigator.clipboard.writeText(link).catch(() => {});
    toast.success("Invite link copied");
  };

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="co-label">Access control</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">Team</h1>
        </div>
        {isAdmin && (
          <button
            data-testid={T.teamInviteBtn}
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press transition-colors duration-200"
          >
            <UserPlus className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            Invite teammate
          </button>
        )}
      </header>

      <div className="p-6 lg:p-10 space-y-8">
        {/* Members table */}
        <div>
          <div className="co-label mb-3">Members · {members.length}</div>
          <div className="border co-hairline bg-white overflow-x-auto">
            <table className="co-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center text-[#52525B]">Loading…</td></tr>
                ) : (
                  members.map((m) => (
                    <tr key={m.id} data-testid={T.teamMemberRow}>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-[#F4F4F5] grid place-items-center rounded-sm">
                            <UserIcon className="h-3.5 w-3.5 text-[#52525B]" strokeWidth={1.75} aria-hidden="true" />
                          </div>
                          <span className="font-semibold">{m.name}</span>
                          {m.id === user?.id && (
                            <span className="text-xs text-[#52525B]">(you)</span>
                          )}
                        </div>
                      </td>
                      <td className="text-[#52525B]">{m.email}</td>
                      <td>
                        <span className={`co-pill border ${roleTone[m.role] || roleTone.staff}`}>
                          {(m.role || "staff").toUpperCase()}
                        </span>
                      </td>
                      <td className="font-mono text-xs text-[#52525B]">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td>
                        {isAdmin && m.role !== "owner" && m.id !== user?.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <Select
                              value={m.role}
                              onValueChange={(v) => changeRole(m.id, v)}
                            >
                              <SelectTrigger className="h-8 w-[110px] text-xs" data-testid={`team-role-${m.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="staff">Staff</SelectItem>
                              </SelectContent>
                            </Select>
                            <button
                              onClick={() => removeMember(m.id)}
                              className="p-2 hover:bg-[#FEF2F2] rounded-sm transition-colors duration-200"
                              data-testid={`team-remove-${m.id}`}
                              title="Remove"
                            >
                              <Trash2 className="h-4 w-4 text-[#B91C1C]" strokeWidth={1.75} aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-[#52525B]">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pending invites */}
        {isAdmin && (
          <div>
            <div className="co-label mb-3">Pending invites · {invites.length}</div>
            <div className="border co-hairline bg-white">
              {invites.length === 0 ? (
                <div className="p-8 text-sm text-[#52525B]">
                  No pending invites. Click <span className="font-semibold">Invite teammate</span> to bring someone in.
                </div>
              ) : (
                invites.map((inv, i) => (
                  <div key={inv.id} className={`flex items-center gap-4 p-5 ${i < invites.length - 1 ? "border-b co-hairline" : ""}`}>
                    <div className="w-10 h-10 bg-[#FFFBEB] text-[#B45309] grid place-items-center rounded-sm">
                      <Mail className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{inv.email}</div>
                      <div className="text-xs text-[#52525B] flex items-center gap-3 mt-0.5">
                        <span>Invited by {inv.invited_by_name}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
                          Expires {new Date(inv.expires_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <span className={`co-pill border ${roleTone[inv.role] || roleTone.staff}`}>
                      {inv.role.toUpperCase()}
                    </span>
                    <button
                      onClick={() => copyLink(inv.token)}
                      className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                      data-testid={T.teamCopyLink}
                      title="Copy invite link"
                    >
                      <Copy className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => revoke(inv.id)}
                      className="p-2 hover:bg-[#FEF2F2] rounded-sm transition-colors duration-200"
                      title="Revoke"
                      data-testid={`team-revoke-${inv.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-[#B91C1C]" strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Role guide */}
        <div className="border co-hairline bg-white p-6 lg:p-8">
          <div className="co-label mb-3 flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-[#4338CA]" strokeWidth={2} aria-hidden="true" />
            Roles
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <div className="font-heading font-bold tracking-tight">Owner</div>
              <div className="text-[#52525B] mt-1">Full control. Cannot be removed or demoted.</div>
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight">Admin</div>
              <div className="text-[#52525B] mt-1">
                Everything except deleting the workspace: invite team, edit payment options, tune reminder rules.
              </div>
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight">Staff</div>
              <div className="text-[#52525B] mt-1">
                Manages clients, sends reminders, marks paid. No access to team, settings, rules or payment methods.
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Invite teammate</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="co-label">Email address</Label>
              <Input
                data-testid={T.teamInviteEmail}
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2"
                placeholder="teammate@business.com"
              />
            </div>
            <div>
              <Label className="co-label">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-2" data-testid={T.teamInviteRole}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full workspace access</SelectItem>
                  <SelectItem value="staff">Staff — clients & reminders only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-[#52525B] bg-[#FAFAFA] border co-hairline p-3">
              A secure invite link is generated and copied to your clipboard. Share it with your teammate — the link is valid for 7 days.
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm font-semibold border border-[#0A0A0B] rounded-sm co-btn-press hover:bg-[#F4F4F5]"
              >
                Cancel
              </button>
              <button
                data-testid={T.teamInviteSubmit}
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-sm co-btn-press disabled:opacity-60"
              >
                {saving ? "Creating…" : "Create invite"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
